import React, { useState } from "react";
import { useSubscribeDetailsByUserIdQuery } from "../../redux/features/user/userApi";
import { useNavigate } from "react-router-dom";

const SubscribeU = ({ id }) => {
  const { data, isLoading, isError, error, refetch } =
    useSubscribeDetailsByUserIdQuery(id);
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);

  if (isLoading) {
    return <div className="py-6 text-center">Loading subscription details...</div>;
  }

  if (isError) {
    return (
      <div className="py-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p className="font-semibold mb-2">Failed to load subscription details</p>
          <p className="text-sm">{error?.data?.message || "Unexpected error"}</p>
          <button
            className="mt-3 px-3 py-1.5 bg-red-600 text-white rounded"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  console.log(`data----29-----,SubscribeU------>`, data?.data);

  const subscriptions = data?.data || [];
  // if (!subscriptions.length) {
  //   return <div className="py-6 text-gray-600">No subscription records found.</div>;
  // }

  const rows = Array.isArray(subscriptions) ? subscriptions : [subscriptions];

  // ✅ Remaining Days (auto updates daily)
  const computeRemainingDays = (endDate) => {
    if (!endDate) return "-";
    const today = new Date();
    const end = new Date(endDate);
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // ✅ Format allocations nicely
  const formatAllocations = (allocations) => {
    if (!allocations) return "-";
    return Object.entries(allocations).map(([coin, data]) => (
      <div key={coin} className="text-xs">
        <strong>{coin.toUpperCase()}</strong> → USD: {data.usd}, Amount:{" "}
        {data.amount}, Price: {data.price}
      </div>
    ));
  };

  const columns = [
    "category",
    "date",



    // "gateway",
    "paymentId",
    "paymentMethod",
    "amount",
    "currency",
    "proof",
    "comment",
    "status",
    "view",

    // "unique_id"
    // "redeemed",
    // "allocations",
    // "remainingdays",
  ];

  const renderCell = (row, key) => {
    if (key === "remainingdays") {
      return `${computeRemainingDays(row.enddate)} Days`;
    }

    if (key === "allocations") {
      return formatAllocations(row.allocations);
    }

    if (key === "redeemed") {
      const value = row.Redeemed ?? row.redeemed;
      return value === true ? "True" : value === false ? "False" : "-";
    }

    if (key === "proof") {
      return row.proof ? (
        <button
          onClick={() => setSelectedImage(row.proof)}
          className="text-blue-600 hover:underline bg-transparent border-none p-0 cursor-pointer"
        >
          View Proof
        </button>
      ) : (
        "-"
      );
    }

    if (key === "view") {
      return (
        <button
          onClick={() => navigate(`/fiat`)}
          className="text-blue-600 hover:underline bg-transparent border-none p-0 cursor-pointer"
        >
          View
        </button>
      );
    }

    if (key === "status") {
      {
        return row.status === 0 ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Rejected
          </span>
        ) : row.status === 1 ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Approved
          </span>
        ) : row.status === 2 ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {row.status}
          </span>
        )
      }

    }

    return row[key] ?? "-";
  };


  // ✅ CSV Export
  const handleExportCsv = () => {
    const header = columns.join(",");

    const dataLines = rows.map((row) =>
      columns
        .map((key) => {
          if (key === "remainingdays") {
            return `"${computeRemainingDays(row.enddate)} Days"`;
          }
          if (key === "allocations") {
            const allocText = Object.entries(row.allocations || {})
              .map(
                ([coin, d]) =>
                  `${coin.toUpperCase()}(USD:${d.usd},Amount:${d.amount},Price:${d.price})`
              )
              .join(" | ");
            return `"${allocText}"`;
          }
          if (key === "proof") {
            return row.proof ? `"${row.proof}"` : `"-"`;
          }
          return `"${row[key] ?? "-"}"`;
        })
        .join(",")
    );

    const csv = [header, ...dataLines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `subscriptions_${id || "user"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full px-2 sm:px-4 md:px-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 p-4 bg-white border rounded shadow">
        <h1 className="text-lg sm:text-2xl font-bold text-blue-900">
          Subscription Details
        </h1>

        <button
          onClick={handleExportCsv}
          className="mt-2 sm:mt-0 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto mt-4">
        <table className="min-w-full border border-gray-200 rounded">
          <thead className="bg-gray-100">
            <tr>
              {columns.map((key) => (
                <th
                  key={key}
                  className="px-3 py-2 text-xs sm:text-sm font-semibold border-b whitespace-nowrap"
                >
                  {key.replace(/_/g, " ").toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="odd:bg-white even:bg-gray-50">
                {columns.map((key) => (
                  <td
                    key={key}
                    className="px-3 py-2 text-xs sm:text-sm border-b align-top"
                  >
                    {renderCell(row, key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={selectedImage}
              alt="Proof"
              className="max-w-full max-h-[80vh] object-contain"
              onError={(e) => {
                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='.3em' fill='%236b7280' font-family='sans-serif' font-size='12'%3EImage not available%3C/text%3E%3C/svg%3E";
              }}
            />
            <div className="p-4 bg-gray-50 border-t">
              <a
                href={selectedImage}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                Open in new tab
              </a>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SubscribeU;
